import { BundleBuilderStoreMixinComponent } from '@theme/bundle-builder-mixin-store';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import {
  validateDynamicBundle,
  getDynamicBundleItems,
  loginShopifyAppProxy,
  updateBundle,
  updateSubscription,
  listSubscriptions,
} from '@theme/bundle-builder-sdk';
import {
  enrichDynamicBundleCartItems,
  getCartDisplayItemCountFromSections,
  getCartItemsComponentSectionIds,
  postCartAddItems,
} from '@theme/bundle-builder-cart';
import { clearBundleEditSnapshot } from '@theme/bundle-builder-edit-session';
import { getActiveThreshold } from '@theme/bundle-builder-tier-config';
import { normalizeShopifyResourceId } from '@theme/gtm-events';

/** @typedef {import('./bundle-builder-store.js').BundleBuilderState} BundleBuilderState */
/** @typedef {import('./bundle-builder-tier-config.js').PurchaseType} PurchaseType */
/** @typedef {import('./bundle-builder-mixin.js').BundleBuilderPropSpec} BundleBuilderPropSpec */
/** @typedef {import('./bundle-builder-sdk.js').RechargeDynamicBundle} RechargeDynamicBundle */
/** @typedef {import('./bundle-builder-sdk.js').RechargeSubscription} RechargeSubscription */

export class BundleBuilderAddToCartComponent extends BundleBuilderStoreMixinComponent {
  requiredRefs = [
    'addToCartButton',
    'addToCartTotal',
    'addToCartLabel',
  ];

  /** @type {boolean} */
  #isAdding = false;

  /**
   * @returns {boolean}
   */
  get isEnabled() {
    if (this.#isAdding || this.state.validationError) return false;
    if (this.state.activeSubscription && !this.getters.hasSubscriptionChanges()) return false;
    return true;
  }

  /**
   * @returns {string|null}
   */
  get validationErrorLabel() {
    let errorLabel = null;
    if (typeof this.state.validationError === 'string') {
      errorLabel = this.state.validationError;
    } else if (this.state.validationError && typeof this.state.validationError === 'object') {
      const collectionId = this.state.validationError.collectionId;
      const productId = this.state.validationError.productId;
      errorLabel = this.state.validationError.type === 'skipOrMinimum' && collectionId 
        ? (this.dataset.labelSkipOrMinimum ?? '').replace('[count]', String(this.state.validationError.quantityToMeetMinimum)).replace('[collectionTitle]', this.state.collections.find(collection => String(collection.id) === collectionId)?.title ?? '') 
        : this.state.validationError.type === 'maxQuantityPerVariant' && productId
          ? (this.dataset.labelMaxQuantityPerVariant ?? '').replace('[count]', String(this.state.validationError.quantityOverMaximum)).replace('[productTitle]', this.getters.getProduct(Number(productId))?.title ?? '')
          : (this.dataset.labelUnderMinimum ?? '').replace('[count]', String(this.state.validationError.quantityToMeetMinimum));
    }
    return errorLabel;
  }

  /**
   * @param {Event} event
   */
  async handleAddToCart(event) {
    event?.preventDefault?.();
    if (!this.isEnabled) return;

    const bundle = this.getters.getDynamicBundle();
    if (!bundle) {
      this.mutations.throwError('Unable to build bundle. Please try again.');
      return;
    }

    const shopifyProductHandle = this.state.bundleProduct?.handle;
    if (!shopifyProductHandle) {
      this.mutations.throwError('Bundle product is missing. Please refresh and try again.');
      return;
    }

    const button = /** @type {HTMLButtonElement} */ (this.refs.addToCartButton);

    this.#isAdding = true;
    this.mutations.clearError();
    this.#renderAddToCart();
    button.setAttribute('aria-busy', 'true');

    /** @type {string[]} */
    let addedCartLineKeys = [];

    try {
      const validation = await validateDynamicBundle(bundle);
      if (validation !== true) {
        this.mutations.throwError(validation);
        return;
      }

      if (this.state.activeSubscription) {
        await this.#updateSubscription(bundle);
        return;
      }

      const rcItems = await getDynamicBundleItems(bundle, shopifyProductHandle);

      const bundleTitle = this.state.bundleProduct?.title || 'Bundle';
      const bundleItems = this.state.items;
      const boxTier = getActiveThreshold();
      const purchaseType = this.state.purchaseType === 'subscribe' ? 'subscription' : 'one_time';
      const deliveryFrequency = this.#resolveSellingPlanName();
      const listId = this.state.collections?.[0]?.handle ?? 'build_your_box';
      const listName = this.state.collections?.[0]?.title ?? 'Build Your Box';
      const gtmParentItemId = normalizeShopifyResourceId(this.state.bundleProduct?.id);

      const parentVariantId = this.getters.getBundleExternalVariantId();

      const itemsWithProps = enrichDynamicBundleCartItems(rcItems, {
        bundleTitle,
        bundleImage: this.state.bundleProduct?.image,
        getQuantity: (variantId) => {
          const bundleItem = bundleItems.find((bi) => Number(bi.variantId) === variantId);
          return bundleItem?.quantity;
        },
        bundleParentHandle: shopifyProductHandle,
        bundleParentVariantId: parentVariantId ?? undefined,
        extraProperties: {
          _purchase_type: purchaseType,
          _delivery_frequency: deliveryFrequency,
          _box_type: "Build Your Box",
          _box_size: boxTier,
          _box_capacity: boxTier,
          _item_list_id: listId,
          _item_list_name: listName,
          _index: "1",
          ...(gtmParentItemId ? { _gtm_item_id: gtmParentItemId } : {}),
        },
      });

      const cartItemComponentsSectionIds = getCartItemsComponentSectionIds();
      const sectionsParam = cartItemComponentsSectionIds.join(',');

      const { ok, response } = await postCartAddItems(itemsWithProps, { sections: sectionsParam });

      if (!ok) {
        this.dispatchEvent(
          new CartErrorEvent(
            this.id,
            String(response.message ?? ''),
            response.description ?? {},
            response.errors ?? {}
          )
        );
        this.mutations.throwError(response.message || 'Unable to add bundle to cart. Please try again.');
        return;
      }

      /** @type {string[]} */
      addedCartLineKeys = (response.items || [])
        .map((/** @type {{ key: string }} */ item) => item?.key)
        .filter((/** @type {string} */ key) => typeof key === 'string');

      /** @type {Record<string, string>} */
      let sections = { ...(response.sections || {}) };

      if (this.state.isEditing && this.state.editCartLineKeys.length > 0) {
        /** @type {Record<string, number>} */
        const updates = {};
        for (const key of this.state.editCartLineKeys) {
          updates[key] = 0;
        }

        const updateBody = JSON.stringify({
          updates,
          sections: sectionsParam,
          sections_url: window.location.pathname,
        });

        const updateRes = await fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body: updateBody }));
        const updateResponse = await updateRes.json();

        if (!updateRes.ok || updateResponse?.status) {
          await this.#removeCartLineKeys(addedCartLineKeys);

          this.dispatchEvent(
            new CartErrorEvent(this.id, updateResponse.message || 'Unable to update bundle in cart.', updateResponse.description, updateResponse.errors)
          );
          this.mutations.throwError('Unable to update bundle in cart. Please try again.');
          return;
        }

        sections = {
          ...sections,
          ...(updateResponse.sections || {}),
        };
      }

      clearBundleEditSnapshot();

      const isEditing = this.state.isEditing;
      const cartDisplayItemCount = isEditing ? getCartDisplayItemCountFromSections(sections) : null;

      if (isEditing) {
        this.#removeEditSearchParam();
      }

      this.dispatchEvent(
        new CartAddEvent(response, this.id, {
          source: isEditing ? 'bundle-builder-update-bundle' : 'bundle-builder-add-to-cart',
          itemCount: isEditing ? (cartDisplayItemCount ?? 0) : 1,
          sections,
        })
      );

      this.mutations.clearBundle();
    } catch (err) {
      if (
        addedCartLineKeys.length > 0 &&
        this.state.isEditing &&
        this.state.editCartLineKeys.length > 0
      ) {
        await this.#removeCartLineKeys(addedCartLineKeys);
      }
      const failureMessage = this.state.activeSubscription
        ? this.getters.isRechargeFrequencyUpdateNotAllowedError(err)
          ? (this.dataset.labelFrequencyNotAllowed ??
              'Delivery frequency cannot be changed here. Enable "Customer can choose any frequency" in Recharge customer portal settings.')
          : 'Unable to update subscription.'
        : 'Unable to add bundle to cart.';
      this.dispatchEvent(new CartErrorEvent(this.id, failureMessage, {}, err));
      this.mutations.throwError(err);
    } finally {
      this.#isAdding = false;
      button.removeAttribute('aria-busy');
      this.#renderAddToCart();
    }
  }

  onStateUpdate() {
    this.#renderAddToCart();
  }

  /**
   * Prefer Shopify catalog cents from the add-ons summary so ATC matches the
   * Add-ons row; fall back to Recharge onetime prices inside the getter.
   *
   * @returns {Record<string, { price: number, compareAtPrice?: number }>}
   */
  #getAddonVariantPrices() {
    const summary = document.querySelector('bundle-builder-addons-summary-component');
    if (!(summary instanceof HTMLElement)) return {};

    const raw = summary.dataset.variantPrices;
    if (!raw) return {};

    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return {};

      /** @type {Record<string, { price: number, compareAtPrice?: number }>} */
      const map = {};
      for (const entry of parsed) {
        if (entry?.id == null) continue;
        map[String(entry.id)] = {
          price: Number(entry.price) || 0,
          compareAtPrice: Number(entry.compareAtPrice) || 0,
        };
      }
      return map;
    } catch {
      return {};
    }
  }

  #renderAddToCart() {
    const button = /** @type {HTMLButtonElement} */ (this.refs.addToCartButton);
    const totalEl = /** @type {HTMLElement} */ (this.refs.addToCartTotal);
    const labelEl = /** @type {HTMLElement} */ (this.refs.addToCartLabel);
    button.disabled = !this.isEnabled;
    totalEl.textContent = this.getters.formatMoney(
      this.getters.getActiveCartTotal(this.#getAddonVariantPrices())
    );

    labelEl.replaceChildren();
    const defaultLabel = this.state.activeSubscription
      ? this.state.subscriptionJustUpdated
        ? (this.dataset.labelSubscriptionUpdated ?? 'Subscription Updated')
        : (this.dataset.labelUpdateSubscription ?? 'Update subscription')
      : this.state.isEditing
        ? (this.dataset.labelUpdate ?? 'Update Bundle')
        : (this.dataset.label ?? 'Add to cart');
    const lines = (this.validationErrorLabel ?? defaultLabel).split('\n');
    for (let i = 0; i < lines.length; i++) {
      if (i > 0) {
        labelEl.appendChild(document.createElement('br'));
      }
      const line = document.createElement('span');
      line.textContent = lines[i] ?? '';
      labelEl.appendChild(line);
    }

    button.classList.toggle('is-loading', this.#isAdding);
  }

  /**
   * Updates the active Recharge subscription bundle instead of adding to cart.
   *
   * @param {RechargeDynamicBundle} bundle
   */
  async #updateSubscription(bundle) {
    const activeSubscription = this.state.activeSubscription;
    if (!activeSubscription?.id) {
      this.mutations.throwError('Subscription is missing. Please refresh and try again.');
      return;
    }

    const externalVariantId = this.getters.getBundleExternalVariantId();
    if (!externalVariantId) {
      this.mutations.throwError('Unable to resolve bundle variant. Please try again.');
      return;
    }

    const bundleProductId = this.state.bundleProduct?.id;
    if (!bundleProductId) {
      this.mutations.throwError('Bundle product is missing. Please refresh and try again.');
      return;
    }

    // Hold steppers for the full save. `commitAddonChanges` alone used to clear
    // `addonSyncing` when add-on writes finished, which unlocked cards during
    // frequency/`updateBundle`/`listSubscriptions`/`loadSubscription` — any
    // local edits in that window were wiped by `clearBundle`.
    this.mutations.setAddonSyncing(true);
    try {
      const session = await loginShopifyAppProxy();
      const frequencyUpdate = this.getters.getSubscriptionFrequencyUpdate({
        sellingPlanId: this.getters.getSelectedSellingPlanId(),
        subscription: activeSubscription,
      });

      if (!frequencyUpdate) {
        this.mutations.throwError('Unable to resolve subscription frequency. Please try again.');
        return;
      }

      // Commit add-ons before frequency/bundle writes. `throwError` does not
      // rethrow — abort when commit refuses (e.g. new add-ons with a null charge
      // date) or an API call fails so we do not persist bundle/frequency changes
      // while the UI reports failure and leaves `subscriptionBaseline` dirty.
      // Also keeps `loadSubscription` from clearing pending local creates; a
      // later successful `loadAddons` `clearError` must not wipe the refusal
      // banner after a false "successful save".
      const addonsCommitted = await this.mutations.commitAddonChanges(session);
      if (!addonsCommitted) return;

      const currentFrequency = this.getters.getSubscriptionFrequencyFromSubscription(activeSubscription);
      const orderFrequencyChanged = this.getters.hasSubscriptionOrderFrequencyChanged(
        currentFrequency,
        frequencyUpdate
      );

      if (orderFrequencyChanged) {
        try {
          await updateSubscription(session, activeSubscription.id, frequencyUpdate);
        } catch (err) {
          if (this.getters.isRechargeFrequencyUpdateNotAllowedError(err)) {
            this.mutations.throwError(
              this.dataset.labelFrequencyNotAllowed ??
                'Delivery frequency cannot be changed here. Enable "Customer can choose any frequency" in Recharge customer portal settings.'
            );
            return;
          }
          throw err;
        }
      }

      await updateBundle(session, activeSubscription.id, {
        external_product_id: { ecommerce: String(bundleProductId) },
        external_variant_id: { ecommerce: externalVariantId },
        items: bundle.selections.map((selection) => ({
          collection_id: selection.collectionId,
          collection_source: 'shopify',
          external_product_id: selection.externalProductId,
          external_variant_id: selection.externalVariantId,
          quantity: selection.quantity,
        })),
      });

      const response = /** @type {{ subscriptions?: RechargeSubscription[] }} */ (
        await listSubscriptions(session, { hydrateBundles: true })
      );
      const updatedSubscription = (response?.subscriptions ?? []).find(
        (subscription) => subscription.id === activeSubscription.id
      );

      if (updatedSubscription) {
        this.mutations.loadSubscription(updatedSubscription, { saved: true });
        return;
      }

      this.mutations.markSubscriptionSaved();
    } finally {
      // Safe even after `loadSubscription` → `clearBundle` (already false).
      // Post-save `loadAddons` only sets syncing on a later tick (after
      // `await getAddonSession`), so this does not race with hydrate.
      this.mutations.setAddonSyncing(false);
    }
  }

  /**
   * Removes cart line items by key. Used to roll back a successful add when a follow-up update fails.
   * @param {string[]} cartLineKeys
   */
  async #removeCartLineKeys(cartLineKeys) {
    if (cartLineKeys.length === 0) return;

    /** @type {Record<string, number>} */
    const updates = {};
    for (const key of cartLineKeys) {
      updates[key] = 0;
    }

    try {
      await fetch(
        `${Theme.routes.cart_update_url}`,
        fetchConfig('json', { body: JSON.stringify({ updates }) })
      );
    } catch {
      // Best-effort rollback; the primary error is surfaced to the customer separately.
    }
  }

  #removeEditSearchParam() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('edit')) return;

    url.searchParams.delete('edit');
    const search = url.searchParams.toString();
    const nextUrl = `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
    history.replaceState(history.state, '', nextUrl);
  }

  /**
   * Human-readable selling plan label for GTM / cart line properties.
   * @returns {string}
   */
  #resolveSellingPlanName() {
    const sellingPlanId = this.state.sellingPlanId;
    if (!sellingPlanId) return '';

    for (const group of this.state.sellingPlanGroups ?? []) {
      for (const plan of group?.selling_plans ?? []) {
        if (this.getters.sellingPlanIdsMatch(plan?.id, sellingPlanId)) {
          return plan?.name ?? '';
        }
      }
    }
    return '';
  }
}

if (!customElements.get('bundle-builder-add-to-cart-component')) {
  customElements.define('bundle-builder-add-to-cart-component', BundleBuilderAddToCartComponent);
}
